/**
 * Wraps a dynamic import with retry logic to handle stale chunk errors
 * after deployments. On failure, retries once with a cache-busting reload.
 */
export function lazyRetry<T>(importFn: () => Promise<T>): Promise<T> {
  return importFn().catch((error) => {
    const isChunkError =
      error?.message?.includes("Failed to fetch dynamically imported module") ||
      error?.message?.includes("Importing a module script failed") ||
      error?.message?.includes("Loading chunk") ||
      error?.message?.includes("error loading dynamically imported module") ||
      error?.name === "ChunkLoadError";

    if (isChunkError) {
      const key = "chunk_retry_at";
      const lastRetry = Number(sessionStorage.getItem(key) || 0);
      const now = Date.now();

      // Only force a hard reload once per 30s to avoid infinite loops
      if (now - lastRetry > 30000) {
        sessionStorage.setItem(key, String(now));
        // Hard reload bypassing cache so the new HTML (with fresh chunk hashes) loads
        window.location.reload();
        // Return a never-resolving promise so React doesn't render an error boundary mid-reload
        return new Promise<T>(() => {});
      }
    }
    throw error;
  });
}
